/**
 * 放送地点に紐づく `contents[]` の編集 UI。
 *
 * - カテゴリのドロップダウン (自列車遅延 / 接続線遅延 / 任意テキスト) を切り替えると、
 *   そのカテゴリに必要な入力フィールドが動的に出現する。
 * - 各コンテンツは「↑」「↓」ボタンで並び替え、「× 削除」ボタンで除去できる。
 * - 「＋ コンテンツを追加」ボタンで末尾にデフォルト値のコンテンツを追加する。
 *
 * 状態管理は親コンポーネント (BroadcastPointForm) が保持し、本コンポーネントは
 * 受け取ったコンテンツ配列を表示・編集して onChange で返すだけのコントロールド
 * コンポーネントとして実装する。
 */
import type {
  BroadcastContent,
  BroadcastContentCategory,
  ConnectingDelayJudgementMode,
  ConnectingLineDelayProperties,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextInputMode,
  FreeTextProperties,
  SelfTrainDelayProperties,
} from '../api/types';

import { useState } from 'react';

import type { RouteMaster } from '../data/routes';
import { sortStopsBySequence, type RouteStop } from '../data/routeStops';

import {
  CATEGORY_META,
  DELAY_BROADCAST_GRANULARITIES,
  GRANULARITY_DEFAULT_SSML,
  cloneContent,
  createDefaultContent,
  getCategoryMeta,
  moveContent,
} from '../utils/broadcastContents';

/**
 * 任意テキスト / SSML の音声プレビュー処理。
 *
 * - `text` または `ssml` のいずれかを受け取り、再生可能な object URL (blob:) を返す。
 * - `text` を SSML として扱う場合は `isSsml: true` を指定する。
 * - 省略時はプレビュー UI を無効化する。
 */
export type PreviewSsmlHandler = (input: {
  text?: string;
  ssml?: string;
  isSsml?: boolean;
}) => Promise<string>;

interface BroadcastContentsEditorProps {
  contents: BroadcastContent[];
  onChange: (next: BroadcastContent[]) => void;
  /**
   * 接続線遅延コンテンツの route_id ドロップダウン用の線区マスタ。
   * 省略時は空配列として扱い、未登録 route_id のみ表示される。
   */
  routes?: RouteMaster[];
  /**
   * 接続線遅延コンテンツの「遅延判定開始駅 / 終了駅」ドロップダウン用の駅順マスタ。
   * route_id をキーに、その路線の駅一覧 (stop_sequence 昇順) を持つ。
   * 親 (BroadcastPointsPage) が ControlPanel API 経由で事前取得して渡す。省略時は空。
   */
  routeStops?: Record<string, RouteStop[]>;
  /**
   * 任意テキスト (free_text) コンテンツの音声ファイルアップロード処理。
   * MP3 File を受け取り、保存された audio_key を返す。省略時はアップロード UI を無効化する。
   */
  onUploadAudio?: (file: File) => Promise<string>;
  /**
   * SSML / 任意テキストの音声プレビュー処理。
   * 省略時はプレビュー UI を無効化する。
   */
  onPreviewSsml?: PreviewSsmlHandler;
}

export function BroadcastContentsEditor({
  contents,
  onChange,
  routes = [],
  routeStops = {},
  onUploadAudio,
  onPreviewSsml,
}: BroadcastContentsEditorProps): React.JSX.Element {
  const updateAt = (index: number, content: BroadcastContent): void => {
    const next = contents.slice();
    next[index] = content;
    onChange(next);
  };

  const removeAt = (index: number): void => {
    const next = contents.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const moveUp = (index: number): void => {
    onChange(moveContent(contents, index, index - 1));
  };
  const moveDown = (index: number): void => {
    onChange(moveContent(contents, index, index + 1));
  };

  const handleAdd = (): void => {
    onChange([...contents, createDefaultContent('self_train_delay')]);
  };

  const handleCategoryChange = (index: number, category: BroadcastContentCategory): void => {
    // カテゴリを変えたら properties は新カテゴリのデフォルト値で初期化する
    updateAt(index, createDefaultContent(category));
  };

  return (
    <div className="contents-editor">
      <div className="contents-editor-header">
        <strong>コンテンツ ({contents.length} 件)</strong>
        <button
          type="button"
          data-testid="add-content-button"
          aria-label="コンテンツを追加"
          onClick={handleAdd}
        >
          ＋ コンテンツを追加
        </button>
      </div>

      {contents.length === 0 ? (
        <div className="empty-state" style={{ padding: '12px 8px' }}>
          コンテンツは設定されていません。「＋ コンテンツを追加」から作成してください。
        </div>
      ) : null}

      <ol className="contents-list">
        {contents.map((content, index) => (
          <li key={index} className="contents-item" data-testid={`content-row-${index}`}>
            <div className="contents-item-header">
              <span className="contents-item-index">#{index + 1}</span>
              {/*
               * カテゴリ <select>。E2E / AI エージェントが画面トップの一覧フィルタ
               * (id="content-filter") と取り違えやすいので、明示的な data-testid を
               * 付与してモーダル内のコンテンツカテゴリ <select> を一意に取得できるようにする。
               * testid は 0 始まりの index を用いる (画面表示の "#i" は 1 始まりだが、
               * 他の content-row-{index} と同じ流儀にする)。
               */}
              <select
                aria-label={`コンテンツ#${index + 1} カテゴリ`}
                data-testid={`content-category-select-${index}`}
                value={content.category}
                onChange={(e) =>
                  handleCategoryChange(index, e.target.value as BroadcastContentCategory)
                }
              >
                {CATEGORY_META.map((m) => (
                  <option key={m.category} value={m.category}>
                    {m.label}
                  </option>
                ))}
              </select>
              <div className="contents-item-actions">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  aria-label={`コンテンツ#${index + 1} を上へ`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === contents.length - 1}
                  aria-label={`コンテンツ#${index + 1} を下へ`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => removeAt(index)}
                  aria-label={`コンテンツ#${index + 1} を削除`}
                >
                  × 削除
                </button>
              </div>
            </div>
            <div className="contents-item-description">
              {getCategoryMeta(content.category)?.description}
            </div>
            <ContentPropertyFields
              content={content}
              routes={routes}
              routeStops={routeStops}
              onUploadAudio={onUploadAudio}
              onPreviewSsml={onPreviewSsml}
              onChange={(c) => updateAt(index, c)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// カテゴリ別プロパティフィールド
// ---------------------------------------------------------------------------

interface ContentPropertyFieldsProps {
  content: BroadcastContent;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  onChange: (content: BroadcastContent) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}

function ContentPropertyFields({
  content,
  routes,
  routeStops,
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: ContentPropertyFieldsProps): React.JSX.Element {
  switch (content.category) {
    case 'self_train_delay':
      return (
        <SelfTrainDelayFields
          props={content.properties as Partial<SelfTrainDelayProperties>}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );
    case 'connecting_line_delay':
      return (
        <ConnectingLineDelayFields
          props={content.properties as Partial<ConnectingLineDelayProperties>}
          routes={routes}
          routeStops={routeStops}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );


    case 'free_text':
      return (
        <FreeTextFields
          props={content.properties as Partial<FreeTextProperties>}
          onUploadAudio={onUploadAudio}
          onPreviewSsml={onPreviewSsml}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );

    default:
      return (
        <div className="error-message">
          未対応のカテゴリ: {(content as { category: string }).category}
        </div>
      );
  }
}

/** 自列車遅延パターンの新規行デフォルト値 (「＋」追加時に使用) */
const DEFAULT_NEW_PATTERN: DelayBroadcastPattern = {
  delay_minutes_from: 0,
  granularity: '1分単位',
};

function SelfTrainDelayFields({
  props,
  onChange,
}: {
  props: Partial<SelfTrainDelayProperties>;
  onChange: (props: SelfTrainDelayProperties) => void;
}): React.JSX.Element {
  // 放送粒度毎の行は固定ではなく、ユーザーが「＋」「−」で追加・削除できる。
  // 既存データをそのまま行として表示し、空の場合は最低 1 行を表示する。
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0 ? existingPatterns : [{ ...DEFAULT_NEW_PATTERN }];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    next[index] = { ...next[index]!, ...patch };
    onChange({ delay_patterns: next });
  };

  /** index の直後に新しい行を挿入する */
  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, { ...DEFAULT_NEW_PATTERN });
    onChange({ delay_patterns: next });
  };

  /** index の行を削除する (最後の 1 行は削除しない) */
  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    onChange({ delay_patterns: next });
  };

  return (
    <div className="contents-fields">
      <table
        className="delay-patterns-table"
        style={{ width: '100%', borderCollapse: 'collapse' }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        放送パターンを省略した場合は放送粒度に対応したデフォルト文言を使用します。
        各行の「＋」で行を追加、「−」で行を削除できます。
      </p>
      {/*
        放送パターン (SSML テンプレート) で使えるプレースホルダの凡例。
        既定文面 (「ご乗車の電車は…」) は変更しないが、カスタムテンプレートでは
        Transformer の route_name 対応で ``{route_name}`` (自路線の一般的な線名) も
        利用できるため、UI 上でも明示する。
      */}
      <p
        className="hint"
        style={{ margin: '4px 0' }}
        data-testid="self-train-delay-route-hint"
      >
        放送パターンには <code>{'{delay_minutes_1}'}</code> /{' '}
        <code>{'{delay_minutes_5}'}</code> / <code>{'{delay_minutes_10}'}</code>
        (放送粒度ごとの切り捨て遅延分数) が使えます。任意で{' '}
        <code>{'{route_name}'}</code> (自路線の一般的な線名 / マスタ未ヒット時は{' '}
        <code>{'{route_id}'}</code> にフォールバック) も利用できます。
      </p>
    </div>
  );
}

function DelayPatternRow({
  index,
  pattern,
  canRemove,
  onUpdate,
  onAdd,
  onRemove,
}: {
  index: number;
  pattern: DelayBroadcastPattern;
  /** 削除可能か (最後の 1 行のときは false。false のとき「−」ボタンを表示しない) */
  canRemove: boolean;
  onUpdate: (patch: Partial<DelayBroadcastPattern>) => void;
  onAdd: () => void;
  onRemove: () => void;
}): React.JSX.Element {
  const granularity = pattern.granularity as DelayBroadcastGranularity;


  return (
    <tr style={{ verticalAlign: 'top', borderTop: '1px solid #ddd' }}>
      {/* 遅れ分数 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ開始分数`}
            value={pattern.delay_minutes_from}
            onChange={(e) =>
              onUpdate({ delay_minutes_from: Math.max(0, Math.floor(Number(e.target.value))) })
            }
          />
          <span>分以上〜</span>
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ終了分数`}
            value={pattern.delay_minutes_to ?? ''}
            placeholder="上限なし"
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({
                delay_minutes_to:
                  val === '' ? undefined : Math.max(0, Math.floor(Number(val))),
              });
            }}
          />
          <span>分未満の遅れ</span>
        </div>
      </td>

      {/* 放送粒度 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <select
          aria-label={`パターン${index + 1} 放送粒度`}
          value={granularity}
          onChange={(e) =>
            onUpdate({ granularity: e.target.value as DelayBroadcastGranularity })
          }
        >
          {DELAY_BROADCAST_GRANULARITIES.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </td>

      {/* 放送パターン (SSMLテンプレート) */}
      <td style={{ padding: '6px', width: '100%' }}>
        {/*
         * data-testid を付与する理由:
         * placeholder には粒度別のデフォルト SSML 文 (例: "<speak>...</speak>") が
         * 動的に入るため、page-summary が拾うラベル (placeholder 優先) が長文化し、
         * AI エージェントが基本情報の「説明」欄 (data-testid=description-input) と
         * 取り違える事象が報告された (Claude が長考の末「{誤った action} 申し訳ありません
         * {正しい action}」と自己訂正レスポンスを返す原因になる)。
         * 明示的な data-testid を付けることで page-summary でも安定したラベル
         * (`self-train-delay-ssml-template-{index}`) として現れ、E2E シナリオからも
         * data-testid 単独で一意に拾えるようにする。
         */}
        <textarea
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box' }}
          aria-label={`パターン${index + 1} 放送パターン`}
          data-testid={`self-train-delay-ssml-template-${index}`}
          value={pattern.ssml_template ?? ''}
          placeholder={GRANULARITY_DEFAULT_SSML[granularity]}
          onChange={(e) =>
            onUpdate({ ssml_template: e.target.value === '' ? undefined : e.target.value })
          }
        />
      </td>

      {/* 操作 (行の追加・削除) */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            type="button"
            className="delay-pattern-add"
            aria-label={`パターン${index + 1} の後に行を追加`}
            title="行を追加"
            onClick={onAdd}
          >
            ＋
          </button>
          {canRemove ? (
            <button
              type="button"
              className="delay-pattern-remove danger"
              aria-label={`パターン${index + 1} を削除`}
              title="行を削除"
              onClick={onRemove}
            >
              −
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/** 接続線遅延の判定モード選択肢 (フロント表示ラベル) */
const CONNECTING_DELAY_MODE_META: ReadonlyArray<{
  mode: ConnectingDelayJudgementMode;
  label: string;
}> = [
  { mode: 'whole_line', label: '接続線全体の遅延' },
  { mode: 'section_scheduled', label: '区間・在線便の遅延' },
  { mode: 'transfer_aware', label: '乗り換え列車の遅延' },
];

function ConnectingLineDelayFields({
  props,
  routes,
  routeStops,
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  const threshold = props.delay_threshold_seconds ?? 0;
  const currentRouteId = props.route_id ?? '';
  // 判定モード。未指定 (既存データ) は接続線全体 (whole_line) として表示する。
  const judgementMode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = props.transfer_time_seconds ?? 180;

  // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
  // 区間 (start/end_stop_id) / 乗換駅 (transfer_stop_id) / 方面 (direction_id) は
  // 判定にも放送文にも使用しないため、UI からも非表示にする。
  const isWholeLineMode = judgementMode === 'whole_line';
  const showSectionFields = !isWholeLineMode; // 開始駅 / 終了駅
  const showTransferStopField = !isWholeLineMode; // 乗換駅
  const showDirectionField = !isWholeLineMode; // 方面


  // マスタに無い route_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownRoute =
    currentRouteId !== '' && !routes.some((r) => r.route_id === currentRouteId);

  // 選択中の接続線 (route_id) に対応する駅一覧 (親が事前取得した routeStops から取り出す)。
  // 念のため stop_sequence 昇順に整列する。
  const stops = sortStopsBySequence(currentRouteId ? routeStops[currentRouteId] : []);

  const startStopId = props.start_stop_id ?? '';

  const endStopId = props.end_stop_id ?? '';

  const transferStopId = props.transfer_stop_id ?? '';

  const directionId = props.direction_id ?? '';

  // 方面ドロップダウンの選択肢は、選択中の接続線 (route_id) の方向マスタ (directions) から生成する。
  // direction_id は方向マスタ内の並び順 (index) を文字列化した値 ("0", "1", ...) として保存する。
  const currentRoute = routes.find((r) => r.route_id === currentRouteId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: String(i),
    label: d.direction_name,
  }));
  // マスタに無い direction_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  return (
    <div className="contents-fields">
      <label>
        <span className="required">接続線 route_id</span>
        <select
          aria-label="接続線 route_id"
          value={currentRouteId}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: e.target.value,
              delay_threshold_seconds: threshold,
              // 路線を変えたら区間 (開始/終了駅) と乗換駅はリセットする
              // (別路線の stop_id が残らないように)
              start_stop_id: '',
              end_stop_id: '',
              transfer_stop_id: '',
            } as ConnectingLineDelayProperties)

          }
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_id}
            </option>
          ))}
          {isUnknownRoute ? (
            <option value={currentRouteId}>{currentRouteId} (マスタ未登録)</option>
          ) : null}
        </select>
      </label>
      <label>
        <span className="required">接続線遅延しきい値 (秒)</span>
        <input
          type="number"
          min={0}
          step={1}
          aria-label="接続線遅延しきい値 (秒)"
          value={threshold}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: Number(e.target.value),
            } as ConnectingLineDelayProperties)
          }
        />
      </label>
      <label>
        <span className="required">判定モード</span>
        <select
          aria-label="判定モード"
          value={judgementMode}
          onChange={(e) => {
            const nextMode = e.target.value as ConnectingDelayJudgementMode;
            // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
            // 切り替え時に区間 / 乗換駅 / 方面 の旧入力を properties から落として、
            // DB / JSON にゴミとして残らないようにする。
            const cleared =
              nextMode === 'whole_line'
                ? {
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                    transfer_stop_id: undefined,
                    direction_id: undefined,
                  }
                : {};
            onChange({
              ...props,
              ...cleared,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: threshold,
              delay_judgement_mode: nextMode,
            } as ConnectingLineDelayProperties);
          }}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      <p className="hint" style={{ margin: '4px 0' }}>
        {judgementMode === 'whole_line'
          ? '接続線全体の最大遅延のみで放送可否を判定します。区間や乗換駅・方面の指定は本モードでは使用しません (他モードに任せてください)。'
          : judgementMode === 'section_scheduled'
            ? '指定区間に定刻在線しているはずの便の遅延で判定します。開始駅・終了駅が必須です。'
            : 'お客さまがまさに乗り換えようとしている接続列車 (自列車到着＋乗換時間以降に発車する便) の遅延で判定します。乗換駅・乗換時間が必須です。'}
      </p>
      {/*
        区間 (開始/終了駅) は section_scheduled でのみ使用する。
        whole_line では「接続線全体の最大遅延」のみで判定する純化されたモードのため非表示。
        transfer_aware は区間ではなく乗換駅で判定するため非表示。
      */}
      {showSectionFields ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定開始駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定開始駅"
                testId="delay-start-stop-select"
                value={startStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    start_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定終了駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定終了駅"
                testId="delay-end-stop-select"
                value={endStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    end_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            開始駅・終了駅を指定すると、その区間で遅延があるときのみ放送します。
          </p>
        </>
      ) : null}

      {/* 乗換時間は transfer_aware モードのときのみ表示する */}
      {judgementMode === 'transfer_aware' ? (
        <label>
          <span className="required">乗換時間 (秒)</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label="乗換時間 (秒)"
            data-testid="transfer-time-seconds-input"
            value={transferTimeSeconds}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                transfer_time_seconds: Math.max(0, Math.floor(Number(e.target.value))),
              } as ConnectingLineDelayProperties)
            }
          />
        </label>
      ) : null}
      {/*
        乗換駅は section_scheduled / transfer_aware で使用する。
        whole_line では路線名のみを読み上げる純化されたモードのため非表示。
      */}
      {showTransferStopField ? (
        <>
          <label>
            <span className={judgementMode === 'transfer_aware' ? 'required' : undefined}>
              乗換駅{judgementMode === 'transfer_aware' ? '' : ' (任意)'}
            </span>
            <StopSelect
              ariaLabel="乗換駅"
              testId="transfer-stop-select"
              value={transferStopId}
              stops={stops}
              disabled={!currentRouteId}
              onChange={(stopId) =>
                onChange({
                  ...props,
                  route_id: props.route_id ?? '',
                  delay_threshold_seconds: threshold,
                  transfer_stop_id: stopId,
                } as ConnectingLineDelayProperties)
              }
            />
          </label>
          <p className="hint" style={{ margin: '4px 0' }}>
            {judgementMode === 'transfer_aware'
              ? '乗り換え先の発車時刻を取得する駅であり、放送文でも読み上げます。'
              : '放送文で読み上げる乗換駅です。未指定の場合は遅延判定開始駅を読み上げます。'}
          </p>
        </>
      ) : null}

      {/*
        方面は section_scheduled / transfer_aware で使用する。
        whole_line では路線全体の遅延を見るため方面の限定は不要 → 非表示。
      */}
      {showDirectionField ? (
        <label>
          <span>方面 (任意)</span>

          <select
            aria-label="方面"
            // 画面トップの線区フィルタにも data-testid="direction-select" / aria-label="方面" の
            // <select> が存在するため、E2E や AI エージェントが取り違えないように
            // モーダル内の方面 <select> には別の testid を付与する。
            data-testid="content-direction-select"
            value={directionId}
            disabled={!currentRouteId}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                direction_id: e.target.value,
              } as ConnectingLineDelayProperties)
            }
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (マスタ未登録)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      <label>
        {/*
         * 画面表示ラベルと aria-label は完全一致させること。
         * 1 つの放送地点に「自列車遅延」と「接続線遅延」の両方を設定するケースがあり、
         * 自列車遅延側にも SSML テンプレート系の <textarea> が存在するため、
         * E2E / AI エージェントが取り違えないように「(接続線遅延)」を必ず付与する。
         * 併せて data-testid="content-ssml-template" でも一意に取得できるようにする。
         */}
        <span>SSML テンプレート (接続線遅延)</span>
        <textarea
          rows={2}
          aria-label="SSML テンプレート (接続線遅延)"
          data-testid="content-ssml-template"
          value={props.ssml_template ?? ''}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: threshold,
              ssml_template: e.target.value,
            } as ConnectingLineDelayProperties)
          }
          placeholder="<speak>接続する{route_name}が遅延しています</speak>"
        />
      </label>
      {/*
        接続線遅延テンプレートで使えるプレースホルダの凡例。
        Issue: route_name 対応 (Transformer 連携) で {route_name} (一般的な線名) と
        {route_id} (ATOS 線名コード) を使い分けられるようになったため、
        UI 上でも違いを明示する。
      */}
      <p className="hint" style={{ margin: '4px 0' }} data-testid="connecting-delay-route-hint">
        <code>{'{route_name}'}</code>
        … 一般的な線名 (例: 「京浜東北線」) /{' '}
        <code>{'{route_id}'}</code>
        … ATOS 線名コード (例: 「京浜」)。
        <br />
        <code>{'{route}'}</code>
        は <code>{'{route_name}'}</code> のエイリアスです。
      </p>
    </div>
  );
}

/**
 * 駅選択ドロップダウン。
 * - stops は stop_sequence 昇順で渡される前提。
 * - value が stops に存在しない (= マスタ未取得 / 別バージョン) 場合も選択肢として残し、
 *   既存値が消えないようにする。
 */
function StopSelect({
  ariaLabel,
  testId,
  value,
  stops,
  disabled,
  onChange,
}: {
  ariaLabel: string;
  /**
   * data-testid 属性。E2E 等から `[data-testid="..."]` で一意に拾うため。
   * aria-label が「乗換駅」「遅延判定開始駅」のように似通っており AI/E2E が
   * 取り違えやすいので、明示的な testid を渡せるようにしている。
   */
  testId?: string;
  value: string;
  stops: RouteStop[];
  disabled?: boolean;
  onChange: (stopId: string) => void;
}): React.JSX.Element {
  const isUnknownStop = value !== '' && !stops.some((s) => s.stop_id === value);
  return (
    <select
      aria-label={ariaLabel}
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">(指定なし)</option>
      {stops.map((s) => (
        <option key={s.stop_id} value={s.stop_id}>
          {s.stop_name || s.stop_id}
        </option>
      ))}
      {isUnknownStop ? <option value={value}>{value} (マスタ未取得)</option> : null}
    </select>
  );
}

/**
 * 既存の properties から入力モードを推測する。
 * - `input_mode` が明示されていればそれを使う。
 * - `audio_key` があれば audio、`ssml` があれば ssml、それ以外は text。
 */
function resolveInputMode(props: Partial<FreeTextProperties>): FreeTextInputMode {
  if (props.input_mode) return props.input_mode;
  if (props.audio_key && props.audio_key.trim() !== '') return 'audio';
  if (props.ssml && props.ssml.trim() !== '') return 'ssml';
  return 'text';
}

const FREE_TEXT_MODE_META: ReadonlyArray<{ mode: FreeTextInputMode; label: string }> = [
  { mode: 'text', label: '通常テキスト' },
  { mode: 'ssml', label: 'SSML' },
  { mode: 'audio', label: '音声ファイル' },
];

/**
 * 入力方法ラジオボタンの data-testid。
 *
 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」のラジオを
 * 安定して選択できるようにするため、各ラジオに明示的な data-testid を付与する。
 *
 * ※ 画面ラベルは「通常テキスト」だが、value 属性は FreeTextInputMode のキー
 *   ("text" / "ssml" / "audio") であり、"plain" のような別名ではない点に注意。
 *   AI エージェントが画面ラベルから value を推測して `value="plain"` 等を
 *   投入する事故を避ける目的でも、testid 経由でのクリックを推奨する。
 */
const FREE_TEXT_MODE_TESTID: Record<FreeTextInputMode, string> = {
  text: 'content-free-text-mode-text',
  ssml: 'content-free-text-mode-ssml',
  audio: 'content-free-text-mode-audio',
};

function FreeTextFields({
  props,
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: {
  props: Partial<FreeTextProperties>;
  onChange: (props: FreeTextProperties) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}): React.JSX.Element {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const mode = resolveInputMode(props);
  const plainText = props.text ?? '';
  const ssmlText = props.ssml ?? '';

  // プレビュー対象 (audio モードはプレビュー不可)
  const previewableText = mode === 'ssml' ? ssmlText : mode === 'text' ? plainText : '';
  const canPreview = mode !== 'audio' && previewableText.trim() !== '';

  const handleModeChange = (next: FreeTextInputMode): void => {
    onChange({ ...props, input_mode: next });
  };

  const handlePreview = async (): Promise<void> => {
    if (!onPreviewSsml || !canPreview) return;
    const input =
      mode === 'ssml' ? { ssml: ssmlText } : { text: plainText, isSsml: false };
    setPreviewError(null);
    setPreviewing(true);
    try {
      // 直前のプレビュー URL は解放しておく
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = await onPreviewSsml(input);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError((err as Error).message || '音声プレビューの生成に失敗しました');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // input を毎回リセットして、同じファイルを連続選択しても onChange が発火するようにする
    e.target.value = '';
    if (!file || !onUploadAudio) return;
    setUploadError(null);
    setUploading(true);
    try {
      const audioKey = await onUploadAudio(file);
      onChange({ ...props, input_mode: 'audio', audio_key: audioKey });
    } catch (err) {
      setUploadError((err as Error).message || '音声ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAudio = (): void => {
    const next = { ...props };
    delete next.audio_key;
    onChange(next);
  };

  return (
    <div className="contents-fields">
      <fieldset
        className="free-text-mode-switch"
        style={{ border: 'none', padding: 0, margin: 0 }}
      >
        <legend style={{ padding: 0 }}>入力方法</legend>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {FREE_TEXT_MODE_META.map((m) => (
            <label
              key={m.mode}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <input
                type="radio"
                /*
                 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」の
                 * ラジオボタンを安定して特定できるよう data-testid を付与する。
                 *
                 * 画面ラベルは日本語 ("通常テキスト" など) だが value は
                 * FreeTextInputMode キー ("text" / "ssml" / "audio") であり、
                 * "plain" のような別名ではない。AI が画面ラベルから推測した
                 * セレクタ (例: input[value='plain']) を投入して 3 連続失敗で
                 * ステップが中止される事故が報告されたため、testid を付ける。
                 */
                data-testid={FREE_TEXT_MODE_TESTID[m.mode]}
                aria-label={`入力方法: ${m.label}`}
                value={m.mode}
                checked={mode === m.mode}
                onChange={() => handleModeChange(m.mode)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </fieldset>

      {mode === 'text' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致させること。
           * 不一致だと E2E / AI エージェントが画面ラベルから要素を特定できず、
           * モーダル内の他のテキスト入力欄 (説明欄: data-testid="description-input") に
           * 誤入力してしまう事象が発生する。
           * 併せて data-testid="content-free-text-plain" でも一意に取得できるようにする。
           */}
          <span>放送文面 (通常テキスト)</span>
          <textarea
            rows={4}
            aria-label="放送文面 (通常テキスト)"
            data-testid="content-free-text-plain"
            value={plainText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'text',
                text: e.target.value,
              })
            }
            placeholder="まもなく終点です"
          />
        </label>
      ) : null}

      {mode === 'ssml' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致 ("放送文面 SSML")。
           * 併せて data-testid="content-free-text-ssml" でも一意に取得できるようにする。
           */}
          <span>放送文面 SSML</span>
          <textarea
            rows={4}
            aria-label="放送文面 SSML"
            data-testid="content-free-text-ssml"
            value={ssmlText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'ssml',
                ssml: e.target.value,
              })
            }
            placeholder="<speak>まもなく終点です</speak>"
          />
        </label>
      ) : null}

      {mode !== 'audio' && onPreviewSsml ? (
        <div className="ssml-preview-field">
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={previewing || !canPreview}
            aria-label="音声プレビュー"
          >
            {previewing ? '生成中...' : '▶ 音声プレビュー'}
          </button>
          {previewUrl ? (
            <audio
              controls
              src={previewUrl}
              data-testid="ssml-preview-audio"
              style={{ display: 'block', marginTop: '4px' }}
            >
              <track kind="captions" />
            </audio>
          ) : null}
          {previewError ? (
            <div className="error-message" role="alert">
              {previewError}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'audio' ? (
        <div className="audio-upload-field">
          <span>放送音声ファイル (MP3)</span>
          {props.audio_key ? (
            <div className="audio-upload-status" data-testid="audio-uploaded">
              <span>アップロード済み: {props.audio_key}</span>
              <button type="button" className="danger" onClick={handleRemoveAudio}>
                音声を削除
              </button>
            </div>
          ) : (
            <p className="hint" style={{ margin: '4px 0' }}>
              MP3 音声ファイルをアップロードしてください。アップロードした音声がそのまま放送されます。
            </p>
          )}
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            aria-label="放送音声ファイル (MP3)"
            disabled={uploading || !onUploadAudio}
            onChange={(e) => void handleFileChange(e)}
          />
          {uploading ? <span className="hint">アップロード中...</span> : null}
          {uploadError ? (
            <div className="error-message" role="alert">
              {uploadError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
